import { CSSProperties, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export type AdminMode = 'dark' | 'light';
const MODE_KEY = 'oc-admin-mode';

const readMode = (): AdminMode => {
  try {
    const saved = localStorage.getItem(MODE_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch {
    /* storage blocked: fall through to the default */
  }
  return 'dark';
};

/**
 * Admin colour mode. Written to <html data-oc-admin> so Radix portals and toasts
 * pick up the tokens too; removed on unmount so the public site is untouched.
 * `forced` pins a mode without changing the saved preference (pages not yet re-themed).
 */
export const useAdminMode = (forced?: AdminMode) => {
  const [mode, setMode] = useState<AdminMode>(readMode);
  const effective = forced ?? mode;

  useLayoutEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-oc-admin', effective);
    return () => html.removeAttribute('data-oc-admin');
  }, [effective]);

  const toggle = useCallback(() => {
    setMode((m) => {
      const next = m === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(MODE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  return { mode: effective, toggle, forced: !!forced };
};

export const usePrefersReducedMotion = () => {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    try {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      setReduce(mq.matches);
      const on = () => setReduce(mq.matches);
      mq.addEventListener('change', on);
      return () => mq.removeEventListener('change', on);
    } catch {
      return undefined;
    }
  }, []);
  return reduce;
};

/**
 * Sliding indicator behind the active item of a nav, segmented control or list.
 * Measures the element matching `activeSelector` inside `container` and returns the
 * indicator's style. Re-measures on resize and once fonts have loaded.
 */
export const useIndicator = <T extends HTMLElement>(activeSelector: string, deps: unknown[], vertical = false) => {
  const ref = useRef<T>(null);
  const [rect, setRect] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  const measure = useCallback(() => {
    const el = ref.current?.querySelector<HTMLElement>(activeSelector);
    const next = el ? { left: el.offsetLeft, top: el.offsetTop, width: el.offsetWidth, height: el.offsetHeight } : null;
    setRect((r) => (r && next && r.left === next.left && r.top === next.top && r.width === next.width && r.height === next.height ? r : next));
  }, [activeSelector]);

  const style: CSSProperties = !rect
    ? { opacity: 0 }
    : vertical
    ? { opacity: 1, width: rect.width, height: rect.height, transform: `translate(${rect.left}px, ${rect.top}px)` }
    : { opacity: 1, width: rect.width, transform: `translateX(${rect.left}px)` };

  useLayoutEffect(measure, [measure, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-subscribes with `deps` because the container may not exist on first mount (the shell
  // shows a spinner until auth resolves), and watches the items too: a web font landing
  // widens them without always resizing the container.
  useEffect(() => {
    const box = ref.current;
    let ro: ResizeObserver | undefined;
    if (box && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(box);
      Array.from(box.children).forEach((c) => ro?.observe(c));
    }
    window.addEventListener('resize', measure);
    document.fonts?.ready.then(measure).catch(() => undefined);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

  return { ref, style, rect };
};

/**
 * Tweens a number: counts up from 0 the first time, then from the current value on change.
 * A timer lands the true value after the tween, because rAF stalls in hidden or throttled
 * tabs (Splitfin once shipped "17" instead of "434").
 */
export const useTween = (value: number, duration = 1400, reduce = false) => {
  const [shown, setShown] = useState(reduce ? value : 0);
  const from = useRef(reduce ? value : 0);

  useEffect(() => {
    if (reduce || !Number.isFinite(value)) {
      setShown(value);
      from.current = value;
      return undefined;
    }
    const start = from.current;
    const t0 = performance.now();
    let raf = 0;
    let done = false;
    const step = () => {
      if (done) return;
      const p = Math.min(1, (performance.now() - t0) / duration);
      const e = 1 - Math.pow(1 - p, 4);
      const v = start + (value - start) * e;
      from.current = v;
      setShown(v);
      if (p < 1) raf = requestAnimationFrame(step);
      else done = true;
    };
    raf = requestAnimationFrame(step);
    const land = window.setTimeout(() => {
      done = true;
      from.current = value;
      setShown(value);
    }, duration + 150);
    return () => {
      done = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(land);
    };
  }, [value, duration, reduce]);

  return shown;
};

/** Element size in CSS pixels (layout box, so a running rise transform cannot skew it). */
export const useSize = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => setSize((s) => (s.w === el.offsetWidth && s.h === el.offsetHeight ? s : { w: el.offsetWidth, h: el.offsetHeight }));
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, ...size };
};
